import { useCallback, useState, type JSX } from "react";
import { ArrowLeftRegular, OpenRegular } from "@fluentui/react-icons";
import { FluentSpinner } from "@/components/fluent-ui";
import { DealTrackerApi } from "@/services/api/deal-tracker-api";
import { useApi } from "@/hooks/useApi";
import { resolveLaunchUrl } from "@/utils/launch-url";
import { DealTracker } from "./DealTracker";
import { DealTrackerTeam } from "./DealTrackerTeam";
import type { MergersAndAcquisitionsRow } from "./deal-tracker-types";
import "./DealTracker.scss";

/** Analysts open first; the Staffer may then open Associates alongside it. */
const DEFAULT_EXPANDED_ROLES = ["Analyst"];

/**
 * The M&A Staffer surface: the team list (TAD-6072) and, in place, one Banker's
 * Deal Tracker (TAD-6073). The tracker itself is the same component the Banker
 * sees, minus "Indicate No Update".
 */
export function DealTrackerStaffer(): JSX.Element {
  const fetcher = useCallback(() => DealTrackerApi.getTeam(), []);
  const { data: roles, loading, error, refetch } = useApi(fetcher);

  // Kept here rather than inside the list so returning from a Banker restores
  // whichever accordions the Staffer left open (TAD-6073 AC 3).
  const [expandedRoles, setExpandedRoles] = useState<string[]>(DEFAULT_EXPANDED_ROLES);
  const [selected, setSelected] = useState<MergersAndAcquisitionsRow | null>(null);
  const [trackerWritten, setTrackerWritten] = useState(false);

  const dashboardUrl = resolveLaunchUrl("dashboard");

  /**
   * Back to the team list. The summary rows -- Last Updated especially -- are a
   * snapshot from the original fetch, so a tracker that was actually written to
   * makes this copy stale; a Banker merely looked at does not.
   */
  const returnToTeam = (): void => {
    setSelected(null);
    if (trackerWritten) {
      setTrackerWritten(false);
      void refetch();
    }
  };

  if (loading) {
    return (
      <div className="deal-tracker__state" role="status">
        <FluentSpinner label="Loading your team…" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="deal-tracker__state" role="alert">
        <p>{error}</p>
        <button type="button" className="deal-tracker__secondary-button" onClick={() => void refetch()}>
          Try again
        </button>
      </div>
    );
  }

  if (selected) {
    return (
      <div className="deal-tracker-staffer">
        <div className="deal-tracker-staffer__back-row">
          <button type="button" className="deal-tracker-staffer__back" onClick={returnToTeam}>
            <ArrowLeftRegular /> Back
          </button>
          <span className="deal-tracker-staffer__banker">{selected.Name}</span>
        </div>
        <DealTracker
          key={selected.UserId}
          banker={selected}
          showIndicateNoUpdate={false}
          onSaved={() => setTrackerWritten(true)}
          onClose={returnToTeam}
        />
      </div>
    );
  }

  const hasTeam = (roles ?? []).some((role) => role.Bankers.length > 0);

  if (!hasTeam) {
    return (
      <div className="deal-tracker__empty">
        <h3>No Deal Trackers to review</h3>
        <p>No M&amp;A Bankers were returned for your dashboard.</p>
        {dashboardUrl ? (
          <a href={dashboardUrl} target="_blank" rel="noreferrer" className="deal-tracker__web-link">
            Open TRIYO dashboard <OpenRegular />
          </a>
        ) : null}
      </div>
    );
  }

  return (
    <div className="deal-tracker-staffer">
      <DealTrackerTeam
        roles={roles ?? []}
        expandedRoles={expandedRoles}
        onToggleRole={(type) =>
          setExpandedRoles((current) =>
            current.includes(type) ? current.filter((open) => open !== type) : [...current, type]
          )
        }
        onSelectBanker={setSelected}
      />
    </div>
  );
}
